import { supabase } from './supabase'
import { nudgeStars } from './starPulse'

/**
 * One Question — a question a week, a short answer, stars for answering.
 * Migration 105, seed 011, `docs/OneConetec.com 52-Week "One Question"
 * Schedule.docx`.
 *
 * ── NOBODY READS ANYBODY ELSE'S ANSWER (131, David 5 Oct) ───────────────────
 *
 * 105 revealed every named answer to everybody the Monday after. It no longer
 * does: a person sees their own answer and a COUNT, and Communications reads
 * the answers on #/support/one-question (lib/oneQuestionComms.ts) to build
 * One Voice, quoting without names. There is deliberately no function in this
 * file that reads another person's answer — the hub has no door to them.
 *
 * ── THE CLIENT DECIDES NOTHING ABOUT TIME ───────────────────────────────────
 *
 * Which question is live, whether last week's answers may be read, and whether
 * an answer is still editable are ALL answered by the database against its own
 * clock (`app.notice_status`, 093). The browser only formats the reveal
 * instant it was handed. Comparing `closesAt` with `new Date()` here would
 * answer with the READER'S clock, and the card and the server would disagree
 * at exactly the boundary — Monday 00:01 — which is the one moment anybody is
 * looking. 093 made that mistake impossible for notices; do not reintroduce it.
 *
 * ── THE STAR AMOUNT IS THE SERVER'S TOO ─────────────────────────────────────
 *
 * `starsForAnswer` is the rate in `star_earning_rule` right now (0 when the
 * rule is off). The schedule document says "10 stars (or whatever amount Dave
 * decides)" — so it will move, and a card still promising 10 after it did is
 * standing rule 4's worst shape.
 */

/** 280, the column's CHECK. The textarea's maxLength mirrors it. */
export const ANSWER_MAX = 280

export type OneQuestionWeek = {
  slot: 'live' | 'last'
  contentId: string
  seq: number
  title: string | null
  body: string | null
  variantLocale: string | null
  isFallback: boolean
  opensAt: string
  closesAt: string
  answerCount: number
  myAnswer: string | null
  myAnswerRemoved: boolean
  starsForAnswer: number
}

export type OneQuestionHub = { live: OneQuestionWeek | null; last: OneQuestionWeek | null }

type HubRow = {
  slot: 'live' | 'last'
  content_id: string
  seq: number
  title: string | null
  body: string | null
  variant_locale: string | null
  is_fallback: boolean
  opens_at: string
  closes_at: string
  answer_count: number
  my_answer: string | null
  my_answer_removed: boolean
  stars_for_answer: number
}

export function shapeHub(rows: HubRow[] | null): OneQuestionHub {
  const out: OneQuestionHub = { live: null, last: null }
  for (const r of rows ?? []) {
    out[r.slot] = {
      slot: r.slot,
      contentId: r.content_id,
      seq: r.seq,
      title: r.title,
      body: r.body,
      variantLocale: r.variant_locale,
      isFallback: r.is_fallback,
      opensAt: r.opens_at,
      closesAt: r.closes_at,
      answerCount: r.answer_count,
      myAnswer: r.my_answer,
      myAnswerRemoved: r.my_answer_removed,
      starsForAnswer: r.stars_for_answer,
    }
  }
  return out
}

export async function loadOneQuestion(): Promise<OneQuestionHub> {
  const { data, error } = await supabase.rpc('one_question_hub')
  if (error) throw error
  return shapeHub(data as HubRow[] | null)
}

export type AnswerResult = { answerId: string; isNew: boolean; starsAwarded: number }

export async function answerOneQuestion(contentId: string, body: string): Promise<AnswerResult> {
  const { data, error } = await supabase.rpc('answer_one_question', {
    p_content_id: contentId,
    p_body: body,
  })
  if (error) throw error
  const row = (data as { answer_id: string; is_new: boolean; stars_awarded: number }[] | null)?.[0]
  if (!row) throw new Error('the answer was not saved')
  if (row.stars_awarded > 0) nudgeStars()
  return { answerId: row.answer_id, isNew: row.is_new, starsAwarded: row.stars_awarded }
}

export async function withdrawOneQuestionAnswer(contentId: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_one_question_answer', { p_content_id: contentId })
  if (error) throw error
}

/**
 * When the week closes — the last moment an answer can be changed — as the
 * reader's calendar sees it. It is an INSTANT (Monday 00:01 Pacific), so the
 * browser's own timezone is the right one to show it in — Lima reads 03:01
 * Monday, which is still Monday. (The name is 105's, from when this instant
 * was also the reveal.)
 */
export function revealLabel(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(d)
}
